/**
 * Onboarding React Query Hooks
 * Epic 13: Central de Ajuda e Suporte
 * Story 13.4: Onboarding Guiado - Custom Hooks
 *
 * 15 hooks for tour and checklist management
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  shouldShowTour,
  getAllTours,
  getTourDefinition,
  getTourForRole,
  getProgress,
  createProgress,
  updateProgress,
  skipTour,
  resetTour,
  completeTour,
  getChecklistItems,
  completeChecklistItem,
  getChecklistDefinitionForRole,
  getTourAnalytics,
  type OnboardingProgress,
  type TourDefinition,
  type ChecklistItem,
  type ChecklistDefinitionItem,
  type ShouldShowResponse,
  type TourAnalytics,
} from "@/services/onboardingApi";

// ============================================================================
// Query Keys
// ============================================================================

export const onboardingKeys = {
  all: ["onboarding"] as const,
  shouldShow: () => [...onboardingKeys.all, "should-show"] as const,
  tours: () => [...onboardingKeys.all, "tours"] as const,
  tour: (tourId: string) => [...onboardingKeys.tours(), tourId] as const,
  tourForRole: (role: string) => [...onboardingKeys.tours(), "role", role] as const,
  progress: (tourId: string) => [...onboardingKeys.all, "progress", tourId] as const,
  checklist: () => [...onboardingKeys.all, "checklist"] as const,
  checklistDefinition: (role: string) =>
    [...onboardingKeys.checklist(), "definition", role] as const,
  analytics: (tourId: string) => [...onboardingKeys.all, "analytics", tourId] as const,
};

// ============================================================================
// Tour Detection Hooks (AC1)
// ============================================================================

/**
 * AC1: Check if onboarding modal should be shown
 * Called on first login and app initialization (only when authenticated)
 */
export const useShouldShowTour = (): UseQueryResult<ShouldShowResponse, Error> => {
  const { isAuthenticated, user } = useAuth();

  return useQuery({
    queryKey: onboardingKeys.shouldShow(),
    queryFn: shouldShowTour,
    staleTime: 0, // Always fresh (depends on user state)
    gcTime: 0, // Don't cache (user-specific)
    // Não dispara durante a troca de senha obrigatória (gate server-side 403a
    // tudo até trocar) — evita ruído de 403 no console.
    enabled: isAuthenticated && !user?.password_change_required,
  });
};

// ============================================================================
// Tour Definition Hooks (AC2, AC5)
// ============================================================================

/**
 * AC5: Get all available tours
 */
export const useAllTours = (): UseQueryResult<TourDefinition[], Error> => {
  return useQuery({
    queryKey: onboardingKeys.tours(),
    queryFn: getAllTours,
    staleTime: 1000 * 60 * 60, // 1 hour (tours rarely change)
  });
};

/**
 * AC2, AC5: Get specific tour definition
 */
export const useTourDefinition = (tourId: string): UseQueryResult<TourDefinition, Error> => {
  return useQuery({
    queryKey: onboardingKeys.tour(tourId),
    queryFn: () => getTourDefinition(tourId),
    staleTime: 1000 * 60 * 60, // 1 hour
    enabled: !!tourId,
  });
};

/**
 * AC2: Get tour for user role
 */
export const useTourForRole = (role: string): UseQueryResult<TourDefinition, Error> => {
  return useQuery({
    queryKey: onboardingKeys.tourForRole(role),
    queryFn: () => getTourForRole(role),
    staleTime: 1000 * 60 * 60, // 1 hour
    enabled: !!role,
  });
};

// ============================================================================
// Progress Hooks (AC2, AC3, AC4)
// ============================================================================

/**
 * AC2: Get user's progress for a tour
 */
export const useProgress = (tourId: string): UseQueryResult<OnboardingProgress, Error> => {
  return useQuery({
    queryKey: onboardingKeys.progress(tourId),
    queryFn: () => getProgress(tourId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!tourId,
  });
};

/**
 * AC2: Create new progress record
 */
export const useCreateProgress = (): UseMutationResult<
  OnboardingProgress,
  Error,
  {
    tour_id: string;
    tour_version: string;
    status?: string;
    current_step?: number;
    completed_steps?: string[];
  }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProgress,
    onSuccess: (data) => {
      // Invalidate progress query
      queryClient.invalidateQueries({ queryKey: onboardingKeys.progress(data.tour_id) });
      // Invalidate should-show query (status changed)
      queryClient.invalidateQueries({ queryKey: onboardingKeys.shouldShow() });
    },
  });
};

/**
 * AC2, AC3: Update progress
 */
export const useUpdateProgress = (
  tourId: string
): UseMutationResult<
  OnboardingProgress,
  Error,
  {
    status?: string;
    current_step?: number;
    completed_steps?: string[];
  }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => updateProgress(tourId, data),
    onMutate: async (newData) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: onboardingKeys.progress(tourId) });

      // Snapshot previous value
      const previousProgress = queryClient.getQueryData<OnboardingProgress>(
        onboardingKeys.progress(tourId)
      );

      // Optimistically update
      if (previousProgress) {
        queryClient.setQueryData<OnboardingProgress>(onboardingKeys.progress(tourId), {
          ...previousProgress,
          ...newData,
        } as OnboardingProgress);
      }

      return { previousProgress };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousProgress) {
        queryClient.setQueryData(onboardingKeys.progress(tourId), context.previousProgress);
      }
    },
    onSuccess: () => {
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: onboardingKeys.progress(tourId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.shouldShow() });
    },
  });
};

/**
 * AC3: Skip tour
 */
export const useSkipTour = (tourId: string): UseMutationResult<OnboardingProgress, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => skipTour(tourId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.progress(tourId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.shouldShow() });
    },
  });
};

/**
 * AC4: Reset tour
 */
export const useResetTour = (
  tourId: string
): UseMutationResult<OnboardingProgress, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => resetTour(tourId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.progress(tourId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.shouldShow() });
    },
  });
};

/**
 * AC2: Complete tour
 */
export const useCompleteTour = (
  tourId: string
): UseMutationResult<OnboardingProgress, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => completeTour(tourId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.progress(tourId) });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.shouldShow() });
    },
  });
};

// ============================================================================
// Checklist Hooks (AC6)
// ============================================================================

/**
 * AC6: Get user's checklist items
 */
export const useChecklistItems = (): UseQueryResult<ChecklistItem[], Error> => {
  return useQuery({
    queryKey: onboardingKeys.checklist(),
    queryFn: getChecklistItems,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * AC6: Complete checklist item
 */
export const useCompleteChecklistItem = (): UseMutationResult<ChecklistItem, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: completeChecklistItem,
    onMutate: async (itemId) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: onboardingKeys.checklist() });

      // Snapshot previous value
      const previousItems = queryClient.getQueryData<ChecklistItem[]>(onboardingKeys.checklist());

      // Optimistically update (mark as completed)
      if (previousItems) {
        const existingItem = previousItems.find((item) => item.checklist_item_id === itemId);
        if (existingItem) {
          queryClient.setQueryData<ChecklistItem[]>(
            onboardingKeys.checklist(),
            previousItems.map((item) =>
              item.checklist_item_id === itemId
                ? { ...item, is_completed: true, completed_at: new Date().toISOString() }
                : item
            )
          );
        }
      }

      return { previousItems };
    },
    onError: (_error, _itemId, context) => {
      // Rollback on error
      if (context?.previousItems) {
        queryClient.setQueryData(onboardingKeys.checklist(), context.previousItems);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.checklist() });
    },
  });
};

/**
 * AC6: Get checklist definition for role
 */
export const useChecklistDefinition = (
  role: string
): UseQueryResult<ChecklistDefinitionItem[], Error> => {
  return useQuery({
    queryKey: onboardingKeys.checklistDefinition(role),
    queryFn: () => getChecklistDefinitionForRole(role),
    staleTime: 1000 * 60 * 60, // 1 hour (definitions rarely change)
    enabled: !!role,
  });
};

// ============================================================================
// Analytics Hooks (AC7 - Admin Only)
// ============================================================================

/**
 * AC7: Get tour analytics (admin only)
 */
export const useTourAnalytics = (tourId: string): UseQueryResult<TourAnalytics, Error> => {
  return useQuery({
    queryKey: onboardingKeys.analytics(tourId),
    queryFn: () => getTourAnalytics(tourId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!tourId,
  });
};

// ============================================================================
// Compound Hooks (Convenience)
// ============================================================================

/**
 * Combined hook for tour lifecycle
 */
export const useTourLifecycle = (tourId: string) => {
  const progress = useProgress(tourId);
  const updateProgressMutation = useUpdateProgress(tourId);
  const skipMutation = useSkipTour(tourId);
  const resetMutation = useResetTour(tourId);
  const completeMutation = useCompleteTour(tourId);

  const nextStep = useCallback(
    (currentStep: number, completedSteps: string[], stepId: string) => {
      updateProgressMutation.mutate({
        current_step: currentStep + 1,
        completed_steps: [...completedSteps, stepId],
      });
    },
    [updateProgressMutation]
  );

  const previousStep = useCallback(
    (currentStep: number) => {
      if (currentStep > 0) {
        updateProgressMutation.mutate({
          current_step: currentStep - 1,
        });
      }
    },
    [updateProgressMutation]
  );

  return {
    progress,
    nextStep,
    previousStep,
    skip: skipMutation.mutate,
    reset: resetMutation.mutate,
    complete: completeMutation.mutate,
    isUpdating: updateProgressMutation.isPending,
  };
};
